<template>
  <div class="legal-page">
    <header class="legal-hero">
      <div class="hero-inner">
        <RouterLink to="/" class="back-link">{{ t('legal.backHome') }}</RouterLink>
        <p class="eyebrow">{{ t('legal.eyebrow') }}</p>
        <h1>{{ document.title }}</h1>
        <p class="hero-copy">{{ document.summary }}</p>
        <p class="updated-at">
          {{ t('legal.updatedLabel') }} {{ t('legal.updatedDate') }}
        </p>
      </div>
    </header>

    <main class="legal-shell">
      <aside class="legal-nav">
        <RouterLink
          to="/privacy"
          class="nav-link"
          :class="{ 'nav-link--active': documentType === 'privacy' }"
        >
          {{ t('legal.navPrivacy') }}
        </RouterLink>
        <RouterLink
          to="/terms"
          class="nav-link"
          :class="{ 'nav-link--active': documentType === 'terms' }"
        >
          {{ t('legal.navTerms') }}
        </RouterLink>
      </aside>

      <article class="legal-card">
        <section
          v-for="(section, sIndex) in document.sections"
          :key="sIndex"
          class="legal-section"
        >
          <h2>{{ rt(section.title) }}</h2>
          <p v-for="(paragraph, pIndex) in section.paragraphs" :key="pIndex">
            {{ rt(paragraph) }}
          </p>
        </section>
      </article>
    </main>
  </div>
</template>

<script>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

export default {
  name: 'LegalDocumentPage',
  props: {
    documentType: {
      type: String,
      required: true,
    },
  },
  setup(props) {
    const { t, tm, rt } = useI18n();
    const documentKey = computed(() =>
      props.documentType === 'terms' ? 'legal.terms' : 'legal.privacy',
    );
    const document = computed(() => ({
      title: t(`${documentKey.value}.title`),
      summary: t(`${documentKey.value}.summary`),
      sections: tm(`${documentKey.value}.sections`),
    }));
    return { t, rt, document };
  },
};
</script>

<style scoped>
.legal-page {
  min-height: 100vh;
  background:
    radial-gradient(circle at top left, rgba(79, 142, 247, 0.08), transparent 32%),
    linear-gradient(180deg, #0d1b2e 0%, #081526 100%);
  color: #e2eaf4;
  font-family: Inter, "Segoe UI", sans-serif;
}

.legal-hero {
  padding: 72px 20px 36px;
}

.hero-inner,
.legal-shell {
  max-width: 1120px;
  margin: 0 auto;
}

.back-link {
  display: inline-flex;
  margin-bottom: 18px;
  color: #4f8ef7;
  text-decoration: none;
  font-weight: 600;
}

.eyebrow {
  margin: 0 0 12px;
  color: #34d399;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  font-size: 13px;
}

.legal-hero h1 {
  margin: 0 0 14px;
  font-size: clamp(36px, 5vw, 58px);
  line-height: 1.02;
}

.hero-copy {
  margin: 0;
  max-width: 760px;
  font-size: 19px;
  line-height: 1.6;
  color: #94adc8;
}

.updated-at {
  margin-top: 16px;
  color: #4a6080;
  font-size: 14px;
}

.legal-shell {
  display: grid;
  grid-template-columns: 260px minmax(0, 1fr);
  gap: 24px;
  padding: 0 20px 64px;
}

.legal-nav {
  position: sticky;
  top: 24px;
  align-self: start;
  display: grid;
  gap: 12px;
}

.nav-link {
  padding: 14px 16px;
  border-radius: 16px;
  background: #152338;
  border: 1px solid #1e3352;
  color: #94adc8;
  text-decoration: none;
  font-weight: 700;
}

.nav-link--active {
  background: #1e3a8a;
  color: #93c5fd;
}

.legal-card {
  padding: 28px;
  border-radius: 28px;
  background: #152338;
  border: 1px solid #1e3352;
  box-shadow: 0 24px 60px -28px rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(12px);
}

.legal-section + .legal-section {
  margin-top: 28px;
  padding-top: 28px;
  border-top: 1px solid #1e3352;
}

.legal-section h2 {
  margin: 0 0 12px;
  font-size: 24px;
}

.legal-section p {
  margin: 0 0 14px;
  color: #94adc8;
  line-height: 1.72;
}

@media (max-width: 900px) {
  .legal-shell {
    grid-template-columns: 1fr;
  }

  .legal-nav {
    position: static;
  }
}

@media (max-width: 640px) {
  .legal-hero {
    padding-top: 48px;
  }

  .legal-card {
    padding: 20px;
    border-radius: 22px;
  }
}
</style>
